import React, { useState } from 'react';
import RegistrationHeader from './components/RegistrationHeader';
import RegistrationProgress from './components/RegistrationProgress';
import RegistrationForm from './components/RegistrationForm';
import OTPVerificationModal from './components/OTPVerificationModal';
import SuccessConfirmation from './components/SuccessConfirmation';
import Icon from '../../components/AppIcon';
import Image from '../../components/AppImage';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import { useNavigate } from 'react-router-dom';
import { otpService } from '../../services/otpService';
import { errorHandler } from '../../utils/errorHandler';

const StudentRegistration = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [showOTPModal, setShowOTPModal] = useState(false);
  const [registrationData, setRegistrationData] = useState(null);
  const [showSuccess, setShowSuccess] = useState(false);
  const [error, setError] = useState('');
  const authContext = useAuth();
  const { signUpStudent } = authContext || {};
  const navigate = useNavigate();
  const toast = useToast();

  const handleFormSubmit = async (formData) => {
    setIsLoading(true);
    setError('');
    setRegistrationData(formData);

    try {
      console.log('🔵 [Registration] Starting OTP send process');
      console.log('🔵 [Registration] Email:', formData?.email);
      console.log('🔵 [Registration] Mobile:', formData?.mobile);

      // Send OTP to email by default
      await otpService?.sendEmailOTP(formData?.email, formData?.fullName);

      console.log('✅ [Registration] OTP sent successfully');
      toast?.success('OTP sent successfully! Please check your email.');
      setIsLoading(false);
      setCurrentStep(2);
      setShowOTPModal(true);
    } catch (err) {
      console.error('❌ [Registration] Failed to send OTP:', err);
      const appError = errorHandler?.parseSupabaseError(err);
      setError(appError?.message);
      setIsLoading(false);
      toast?.error(appError?.message);
      errorHandler?.logError(appError, { context: 'OTP Send', email: formData?.email });
    }
  };

  const handleOTPVerify = async (otp, verificationType) => {
    setIsLoading(true);
    setError('');

    try {
      console.log('🔵 [Registration] Verifying OTP');

      let identifier = verificationType === 'email' ? registrationData?.email : registrationData?.mobile;

      // Use the new async verifyOTPAsync method
      const verificationResult = await otpService?.verifyOTPAsync(identifier, verificationType, otp);

      if (!verificationResult?.success) {
        const errorMsg = verificationResult?.error || 'OTP verification failed';
        setError(errorMsg);
        setIsLoading(false);
        toast?.error(errorMsg);
        throw new Error(errorMsg);
      }

      setError('');
      console.log('✅ [Registration] OTP verified successfully');
      console.log('🔵 [Registration] Creating user account...');

      if (!signUpStudent || typeof signUpStudent !== 'function') {
        const errorMsg = 'Sign up function is not available. Please refresh the page and try again.';
        setError(errorMsg);
        setIsLoading(false);
        toast?.error(errorMsg);
        throw new Error(errorMsg);
      }

      const { data, error: signUpError } = await signUpStudent({
        email: registrationData?.email,
        password: registrationData?.password,
        fullName: registrationData?.fullName,
        mobile: registrationData?.mobile,
        city: registrationData?.city,
        state: registrationData?.state,
        courseInterest: registrationData?.courseInterest,
        examStatus: registrationData?.examStatus,
        budgetRange: registrationData?.budgetRange,
        studyMode: registrationData?.studyMode,
        consentGiven: registrationData?.dataConsent,
        utmSource: registrationData?.utmSource || 'direct',
        utmCampaign: registrationData?.utmCampaign || 'organic'
      });

      if (signUpError) {
        const appError = errorHandler?.parseSupabaseError(signUpError);
        setError(appError?.message);
        setIsLoading(false);
        toast?.error(appError?.message);
        throw appError;
      }

      console.log('✅ [Registration] Account created successfully');
      toast?.success('Account created successfully! Redirecting to your dashboard...');
      setIsLoading(false);
      setShowOTPModal(false);

      setTimeout(() => {
        navigate('/student-dashboard', { replace: true });
      }, 1500);
    } catch (err) {
      console.error('❌ [Registration] Registration failed:', err);
      const appError = errorHandler?.parseSupabaseError(err);
      setError(appError?.message);
      setIsLoading(false);
      throw err;
    }
  };

  const handleCloseOTPModal = () => {
    if (!isLoading) {
      setShowOTPModal(false);
      setCurrentStep(1);
    }
  };

  if (showSuccess && registrationData) {
    return (
      <SuccessConfirmation
        email={registrationData?.email}
        mobile={registrationData?.mobile} />);


  }

  return (
    <div className="min-h-screen bg-background">
      <RegistrationHeader />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-12">
        <RegistrationProgress currentStep={currentStep} />

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
          <div className="order-2 lg:order-1">
            <div className="bg-card rounded-xl shadow-lg border border-border p-6 md:p-8">
              <div className="mb-6">
                <h1 className="text-2xl md:text-3xl font-bold text-foreground mb-2">
                  Create Your Account
                </h1>
                <p className="text-sm md:text-base text-muted-foreground">
                  Join thousands of students exploring educational opportunities
                </p>
              </div>

              {error &&
              <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
                  <Icon name="AlertCircle" size={20} color="#dc2626" />
                  <p className="text-sm text-red-800">{error}</p>
                </div>
              }

              <RegistrationForm
                onSubmit={handleFormSubmit}
                isLoading={isLoading} />
              
              

            </div>
          </div>

          <div className="order-1 lg:order-2 space-y-6">
            <div className="bg-gradient-to-br from-primary/10 to-secondary/10 rounded-xl p-6 md:p-8 border border-primary/20">
              <div className="flex items-start gap-4 mb-6">
                <div className="flex-shrink-0 w-12 h-12 bg-primary/20 rounded-lg flex items-center justify-center">
                  <Icon name="Sparkles" size={24} color="var(--color-primary)" />
                </div>
                <div>
                  <h2 className="text-xl md:text-2xl font-bold text-foreground mb-2">
                    Why Join Studyexpo?
                  </h2>
                  <p className="text-sm text-muted-foreground">
                    Your gateway to discovering the perfect educational institution
                  </p>
                </div>
              </div>

              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 w-8 h-8 bg-primary/20 rounded-lg flex items-center justify-center">
                    <Icon name="Building2" size={18} color="var(--color-primary)" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-foreground mb-1">Top Institutes</h3>
                    <p className="text-sm text-muted-foreground">
                      Explore virtual booths from top educational institutions worldwide
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 w-8 h-8 bg-primary/20 rounded-lg flex items-center justify-center">
                    <Icon name="MessageSquare" size={18} color="var(--color-primary)" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-foreground mb-1">Live Interactions</h3>
                    <p className="text-sm text-muted-foreground">
                      Chat directly with admission counselors and get instant answers
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 w-8 h-8 bg-primary/20 rounded-lg flex items-center justify-center">
                    <Icon name="Video" size={18} color="var(--color-primary)" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-foreground mb-1">Video Counseling</h3>
                    <p className="text-sm text-muted-foreground">
                      Schedule one-on-one video sessions with institute representatives
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 w-8 h-8 bg-primary/20 rounded-lg flex items-center justify-center">
                    <Icon name="Download" size={18} color="var(--color-primary)" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-foreground mb-1">Resources & Brochures</h3>
                    <p className="text-sm text-muted-foreground">
                      Download course details, application forms, and prospectus instantly
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-card rounded-xl overflow-hidden border border-border">
              <div className="aspect-video relative overflow-hidden">
                <Image
                  src="/assets/images/education_illustration_neutral.png"
                  alt="Education themed illustration with graduation cap, open book, and academic icons representing learning and academic excellence"
                  className="w-full h-full object-cover" />

              </div>
              <div className="p-6">
                <div className="flex items-center gap-3 mb-3">
                  <div className="flex -space-x-2">
                    <div className="w-8 h-8 rounded-full bg-primary/20 border-2 border-card flex items-center justify-center">
                      <Icon name="Users" size={16} color="var(--color-primary)" />
                    </div>
                    <div className="w-8 h-8 rounded-full bg-success/20 border-2 border-card flex items-center justify-center">
                      <Icon name="TrendingUp" size={16} color="var(--color-success)" />
                    </div>
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-foreground">20,000+ Students</p>
                    <p className="text-xs text-muted-foreground">Already registered</p>
                  </div>
                </div>
                <p className="text-sm text-muted-foreground">
                  Join our growing community of students finding their perfect educational match
                </p>
              </div>
            </div>

            <div className="bg-accent/10 rounded-xl p-6 border border-accent/20">
              <div className="flex items-start gap-3">
                <Icon name="Shield" size={24} color="var(--color-accent)" />
                <div>
                  <h3 className="font-semibold text-foreground mb-1">Your Data is Secure</h3>
                  <p className="text-sm text-muted-foreground">
                    We use industry-standard encryption and comply with GDPR regulations to protect your personal information
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <OTPVerificationModal
        isOpen={showOTPModal}
        onClose={handleCloseOTPModal}
        onVerify={handleOTPVerify}
        email={registrationData?.email}
        mobile={registrationData?.mobile}
        isLoading={isLoading}
        error={error} />
      
      
      

    </div>);

};

export default StudentRegistration;